import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import CLUB from '../config/club.js'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import { useDbValue } from '../lib/useDbValue.js'
import DataGate from '../components/DataGate.jsx'
import { normalizeTeamKey, submitTeamQualifying } from '../lib/data.js'
import { parsFor, DEFAULT_PARS } from '../lib/scoring.js'
import { lastNames } from '../lib/teamName.js'
import { createQualifyingEventTeam, submitQualifyingEventResult, submitTeamQualifyingEvent } from '../lib/qualifyingEvents.js'
import { teamIsQualified, qualifyingOpen } from '../lib/qualifyStatus.js'
import TokenQualify, { TeamQrImage } from './TokenQualify.jsx'
import { getAppTarget } from '../config/appLinks.js'

// ── /play — the companion player flow (PBCCGolfGames wraps this route) ───────
// ONE mobile route that moves a player's team through the whole event by
// reading its status in the database. States, in order:
//   QUALIFY   → enter team + score, "Go" saves the team to the qualifying event
//   WAITING   → submitted; waiting for the director to release the field
//   ASSIGNED  → field released → opponent / time / hole / format + "on the tee"
//   PLAY      → "I'm on the tee box" opens the existing /score scorecard
//   REPEAT    → match final + next pairing set → back to ASSIGNED (new match)
//
// Entry: the qualifying QR opens /play?q={eventId}. The eventId is remembered
// on the device (localStorage) so the QR is a ONE-TIME way in — reopening the
// app returns straight to the current state, no QR needed again.
//
// BOTH players' phones link to the SAME team: the first creates it via "Go";
// the second taps "Join your team". Both then track and open the same team
// scorecard (the scorecard is keyed by team, not by phone).

// USER-DRIVEN light/dark. The visible toggle (pill in the header) remembers the
// choice per device in localStorage and drives the theme; if the user hasn't
// chosen yet it falls back to the OS setting. Dark keeps the branding: deep
// forest → near-black page, dimmed parchment cards, gold trim. GREEN stays dark
// so it reads as page bg AND as dark text on the card.
const THEME_KEY = 'pbcc_theme'
function readThemeMode() {
  try { const m = localStorage.getItem(THEME_KEY); return m === 'light' || m === 'dark' ? m : null } catch { return null }
}
const DARK = readThemeMode()
  ? readThemeMode() === 'dark'
  : (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
function toggleTheme() {
  try { localStorage.setItem(THEME_KEY, DARK ? 'light' : 'dark') } catch { /* ignore */ }
  window.location.reload()
}
const GREEN = DARK ? '#0B140E' : 'var(--ge-primary)'
const CREAM = DARK ? '#E7E0CE' : 'var(--ge-cream-premium)'
const GOLD = 'var(--ge-gold)'
const EVENT_KEY = 'play_event'                       // last event this device scanned
const linkKey = (eventId) => `play_link_${eventId}`  // { qTeamId, teamName }

function readLink(eventId) {
  if (!eventId) return null
  try { return JSON.parse(localStorage.getItem(linkKey(eventId)) || 'null') } catch { return null }
}
function writeLink(eventId, link) {
  try { localStorage.setItem(linkKey(eventId), JSON.stringify(link)) } catch { /* ignore */ }
}
function clearLink(eventId) {
  try { localStorage.removeItem(linkKey(eventId)) } catch { /* ignore */ }
}

// Find this team's flight + tournament team id by matching the canonical team
// key (same normalization the build pipeline uses). Returns null until found.
function findTournamentTeam(tournament, teamName) {
  const want = normalizeTeamKey(teamName)
  if (!want) return null
  const flights = tournament?.flights || {}
  for (const [flightId, flight] of Object.entries(flights)) {
    const teams = flight?.teams || {}
    for (const [teamId, team] of Object.entries(teams)) {
      if (normalizeTeamKey(team?.info?.name) === want) return { flightId, teamId, team, flight }
    }
  }
  return null
}

// Derive the team's CURRENT match from the bracket. The current match is the
// highest-round appearance of the team — so once the director advances the
// bracket, the team's new match becomes current automatically (REPEAT).
function deriveMatch(tournament, flightId, teamId) {
  const rounds = tournament?.brackets?.[flightId]?.rounds
  if (!rounds) {
    // No bracket → fall back to the flight's opponentId pairing (match play).
    const oppId = tournament?.flights?.[flightId]?.teams?.[teamId]?.info?.opponentId || null
    if (oppId) return { state: 'assigned', roundLabel: 'Match Play', opponentId: oppId }
    return { state: 'waiting_next', roundLabel: '', opponentId: null }
  }
  const ordered = Object.entries(rounds).sort((a, b) => (a[1]?.order ?? 0) - (b[1]?.order ?? 0))
  const maxOrder = ordered.length ? (ordered[ordered.length - 1][1]?.order ?? 0) : 0
  const appearances = []
  for (const [, r] of ordered) {
    for (const [, m] of Object.entries(r?.matches || {})) {
      if (m && (m.a === teamId || m.b === teamId)) {
        appearances.push({ order: r?.order ?? 0, label: r?.label || 'Match', match: m })
      }
    }
  }
  if (!appearances.length) return { state: 'not_seeded', roundLabel: '', opponentId: null }
  appearances.sort((a, b) => a.order - b.order)
  const cur = appearances[appearances.length - 1]
  const m = cur.match
  const oppId = m.a === teamId ? m.b : m.a
  const hasOpponent = !!(m.a && m.b)
  if (!m.winnerId) {
    return hasOpponent
      ? { state: 'assigned', roundLabel: cur.label, opponentId: oppId }
      : { state: 'waiting_next', roundLabel: cur.label, opponentId: null }
  }
  // Current match is final:
  if (m.winnerId === teamId) {
    if (cur.order >= maxOrder) return { state: 'champion', roundLabel: cur.label, opponentId: oppId }
    return { state: 'waiting_next', roundLabel: cur.label, opponentId: oppId } // won; next pairing not set yet
  }
  return { state: 'eliminated', roundLabel: cur.label, opponentId: oppId }
}

// teamIsQualified() + qualifyingOpen() now live in ../lib/qualifyStatus.js so the
// global "Start Qualifying" follow pill shares the exact same pending test.

function findMatchTime(tournament, teamId) {
  const days = tournament?.teeTimes || {}
  for (const day of Object.values(days)) {
    for (const w of Object.values(day?.waves || {})) {
      if (w?.assignments && teamId in w.assignments) return w.startTime || null
    }
  }
  return null
}

// TEST: a token deep link (/play?t=<tid>&tk=<token>) binds the companion app
// DIRECTLY to a team — no qualifying event, no name/email. Falls back to a
// remembered token so the QR is a one-time scan. Otherwise the normal
// qualifying-event flow runs unchanged (and the club never sees this branch).
const PLAY_TOKEN_KEY = 'play_token'
export default function PlayApp() {
  const [params] = useSearchParams()
  let eid = params.get('e')
  let tid = params.get('t')
  let token = params.get('tk')
  // Remember the token so the saved QR / installed icon reopens to the team.
  if (PBCC_PREMIUM && (!token || (!eid && !tid))) {
    try {
      const s = JSON.parse(localStorage.getItem(PLAY_TOKEN_KEY) || 'null')
      if (s?.token && (s.eid || s.tid)) { eid = s.eid || null; tid = s.tid || null; token = s.token }
    } catch { /* ignore */ }
  }
  // A team-QR token scan → show the "Install the App / Use Web App" gate FIRST
  // (mobile, not yet installed), then continue into the live team flow. Event
  // token (the real qualify path) wins; then the tournament token.
  if (PBCC_PREMIUM && token && (eid || tid)) {
    const entry = eid
      ? <EventQualifyEntry eventId={eid} token={token} />
      : <TokenTeamEntry tid={tid} token={token} />
    // This team's own play link — stashed to the clipboard for the freshly
    // installed Android app so it opens straight onto this team (auto-bind).
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const playHref = eid
      ? `${origin}/play?e=${encodeURIComponent(eid)}&tk=${encodeURIComponent(token)}`
      : `${origin}/play?t=${encodeURIComponent(tid)}&tk=${encodeURIComponent(token)}`
    return <AppInstallGate playHref={playHref}>{entry}</AppInstallGate>
  }
  return <QualifyingPlayFlow />
}

// ── "Install the App / Use Web App" gate ─────────────────────────────────────
// The first screen a team QR lands on. Offers the native app (iPhone → App Store,
// Android → APK) OR "Use Web App" to continue in the browser into the SAME live
// team flow. Skipped entirely on desktop, when already installed (PWA/standalone),
// or inside the native webview — and once "Use Web App" is chosen this session.
function AppInstallGate({ children, playHref }) {
  const [webChosen, setWebChosen] = useState(() => {
    try { return sessionStorage.getItem('pbcc_use_web') === '1' } catch { return false }
  })
  const target = getAppTarget()
  if (!target || webChosen) return children
  return (
    <InstallOrWebPage
      target={target}
      playHref={playHref}
      onUseWeb={() => { try { sessionStorage.setItem('pbcc_use_web', '1') } catch { /* ignore */ } setWebChosen(true) }}
    />
  )
}

function InstallOrWebPage({ target, playHref, onUseWeb }) {
  const ios = target.platform === 'ios'
  // ANDROID one-scan auto-bind: stash this team's play link on the clipboard the
  // instant they tap Install, so the freshly installed app reads it on first
  // launch and opens straight onto this team — no second in-app scan. Best-effort
  // (needs the clipboard to survive the install); the in-app scan is the fallback.
  // iPhone keeps Step 2 (App Store install + in-app scan), so skip it there.
  const onInstall = () => {
    if (!ios && playHref) {
      try { navigator.clipboard?.writeText('pbcc4ball:team=' + playHref) } catch { /* ignore */ }
    }
  }
  return (
    <div style={{ minHeight: '100%', background: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '28px 18px' }}>
      <div style={{ width: '100%', maxWidth: 380, textAlign: 'center' }}>
        {CLUB.logoLight ? <img src={CLUB.logoLight} alt="" style={{ height: 96, margin: '0 auto 16px', display: 'block', objectFit: 'contain' }} /> : null}
        <h1 style={{ color: CREAM, fontSize: 27, fontWeight: 900, margin: '0 0 6px', letterSpacing: '-0.01em' }}>PBCC 4‑Ball</h1>
        <p style={{ color: CREAM, opacity: 0.85, fontSize: 15, lineHeight: 1.45, margin: '0 0 28px' }}>
          Get the best experience with the app — or continue right here in your browser.
        </p>
        <a
          href={target.href}
          onClick={onInstall}
          {...(ios ? { target: '_blank', rel: 'noreferrer' } : {})}
          style={{ display: 'block', background: GOLD, color: 'var(--ge-primary)', fontWeight: 900, fontSize: 18, padding: '16px', borderRadius: 14, textDecoration: 'none', marginBottom: 12, boxShadow: '0 8px 22px rgba(0,0,0,0.35)' }}>
          {ios ? ' Install the App' : '⤓ Install the App'}
        </a>
        <button
          onClick={onUseWeb}
          style={{ display: 'block', width: '100%', background: 'transparent', color: CREAM, fontWeight: 800, fontSize: 16, padding: '15px', borderRadius: 14, border: `2px solid ${CREAM}`, cursor: 'pointer' }}>
          Use Web App →
        </button>
        <p style={{ color: CREAM, opacity: 0.6, fontSize: 12, marginTop: 16 }}>
          {ios ? 'Install opens the App Store.' : 'Install downloads the Android app.'}
        </p>
      </div>
    </div>
  )
}

// Qualifying EVENT token entry — the per-team QR that lives INSIDE the qualifying
// event. Resolves the token → event team, then runs the shared qualify UI, which
// submits the score back onto the event team (flagged pending for manual entry).
function EventQualifyEntry({ eventId, token }) {
  useEffect(() => {
    try { localStorage.setItem(PLAY_TOKEN_KEY, JSON.stringify({ eid: eventId, token })) } catch { /* ignore */ }
  }, [eventId, token])
  const { data: event, status, error, retry } = useDbValue(eventId ? `qualifyingEvents/${eventId}` : null)
  if (status === 'loading' || status === 'error') {
    return <Shell><Card><DataGate status={status} error={error} retry={retry} label="Loading qualifying…" /></Card></Shell>
  }
  const ref = event?.teamTokens?.[token]
  const team = ref ? event?.teams?.[ref.teamId] : null
  if (!ref || !team) {
    return <Shell><Card>
      <Badge tone="muted">Qualifying</Badge>
      <h1 className="text-2xl font-extrabold mt-2 mb-1" style={{ color: GREEN }}>Link not recognized</h1>
      <p className="text-gray-600">This qualifying link isn’t valid. Please check with the pro shop for your team’s QR code.</p>
    </Card></Shell>
  }
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const players = (ref.players && ref.players.length ? ref.players : [team.player1, team.player2]).filter(Boolean)
  const teamName = ref.teamName || team.teamName || ref.teamId
  return (
    <TokenQualify
      teamName={teamName}
      players={players}
      pars={DEFAULT_PARS}
      teamLink={`${origin}/play?e=${encodeURIComponent(eventId)}&tk=${encodeURIComponent(token)}`}
      storageKey={`tqe_${eventId}_${ref.teamId}`}
      onSubmit={(p) => submitTeamQualifyingEvent(eventId, ref.teamId, { ...p, teamName })}
    />
  )
}

function QualifyingPlayFlow() {
  const [params] = useSearchParams()
  const navigate = useNavigate()

  // Event id: from the scanned QR (?q=), otherwise the one this device
  // remembered. Persist a scanned id so the QR is only needed once.
  const eventId = useMemo(() => {
    const q = params.get('q')
    if (q) { try { localStorage.setItem(EVENT_KEY, q) } catch { /* ignore */ } return q }
    try { return localStorage.getItem(EVENT_KEY) || '' } catch { return '' }
  }, [params])

  const { data: qevent, status: qStatus, error: qError, retry: qRetry } = useDbValue(eventId ? `qualifyingEvents/${eventId}` : null)
  const builtTid = qevent?.info?.builtTournamentId || null
  const { data: tournament } = useDbValue(builtTid ? `tournaments/${builtTid}` : null)

  const [link, setLink] = useState(() => readLink(eventId))
  useEffect(() => { setLink(readLink(eventId)) }, [eventId])

  if (!eventId) {
    return <Shell><Card><h1 className="text-2xl font-extrabold mb-2" style={{ color: GREEN }}>Welcome</h1>
      <p className="text-gray-600">Scan your qualifying QR code to begin.</p></Card></Shell>
  }
  if (qStatus === 'loading' || qStatus === 'error') {
    return <Shell><Card><DataGate status={qStatus} error={qError} retry={qRetry} label="Loading…" /></Card></Shell>
  }

  const eventName = qevent?.info?.name || CLUB.name

  // ── QUALIFY — no team linked to this phone yet ────────────────────────────
  if (!link) {
    return (
      <Shell>
        <QualifyScreen
          eventId={eventId}
          eventName={eventName}
          teams={qevent?.teams || {}}
          onLinked={(l) => setLink(l)}
        />
      </Shell>
    )
  }

  const switchTeam = () => { clearLink(eventId); setLink(null) }

  // ── WAITING — submitted, field not released yet ───────────────────────────
  if (!builtTid) {
    return (
      <Shell>
        <Card>
          <Badge>Qualifying</Badge>
          <h1 className="text-2xl font-extrabold mt-2 mb-1" style={{ color: GREEN }}>Qualification Submitted</h1>
          <p className="text-gray-600">Tournament assignments will appear here when released.</p>
          <TeamLine name={link.teamName} />
          <SwitchLink onClick={switchTeam} />
        </Card>
      </Shell>
    )
  }

  // Field released — locate this team in the built tournament.
  const found = tournament ? findTournamentTeam(tournament, link.teamName) : null
  if (tournament && !found) {
    return (
      <Shell>
        <Card>
          <Badge tone="muted">Field released</Badge>
          <h1 className="text-2xl font-extrabold mt-2 mb-1" style={{ color: GREEN }}>Not in the field</h1>
          <p className="text-gray-600">The field has been released and your team isn’t listed. Please check with the pro shop.</p>
          <TeamLine name={link.teamName} />
          <SwitchLink onClick={switchTeam} />
        </Card>
      </Shell>
    )
  }
  if (!found) {
    return <Shell><Card><DataGate status="loading" label="Loading assignments…" /></Card></Shell>
  }

  const { flightId, teamId, team, flight } = found
  return (
    <TeamStatusView
      tournament={tournament} tid={builtTid}
      flightId={flightId} teamId={teamId} team={team} flight={flight}
      teamName={link.teamName} onSwitch={switchTeam}
    />
  )
}

// ── Token deep-link entry (TEST) — resolve the token → team, then render the
// same status view the qualifying flow uses. The token is remembered so the QR
// is a one-time scan; there is no name entry.
function TokenTeamEntry({ tid, token }) {
  const [tqParams] = useSearchParams()
  const meKey = `play_me_${token}`
  const [mePlayer, setMePlayer] = useState(() => { try { return localStorage.getItem(meKey) || '' } catch { return '' } })
  // A not-yet-qualified team lands on their team home first and browses freely;
  // this flips true only when they tap "Start Qualifying" there.
  const [qualifyingNow, setQualifyingNow] = useState(false)
  useEffect(() => {
    try { localStorage.setItem(PLAY_TOKEN_KEY, JSON.stringify({ tid, token })) } catch { /* ignore */ }
  }, [tid, token])
  const { data: tournament, status, error, retry } = useDbValue(tid ? `tournaments/${tid}` : null)
  if (status === 'loading' || status === 'error') {
    return <Shell><Card><DataGate status={status} error={error} retry={retry} label="Loading your team…" /></Card></Shell>
  }
  const ref = tournament?.teamTokens?.[token]
  const flight = ref ? tournament?.flights?.[ref.flightId] : null
  const team = flight?.teams?.[ref?.teamId]
  if (!ref || !team) {
    return <Shell><Card>
      <Badge tone="muted">Team link</Badge>
      <h1 className="text-2xl font-extrabold mt-2 mb-1" style={{ color: GREEN }}>Link not recognized</h1>
      <p className="text-gray-600">This team link isn’t valid for the current tournament. Please check with the pro shop for your QR code.</p>
    </Card></Shell>
  }
  // WHICH PLAYER ARE YOU? — after landing on the team, pick your player before
  // continuing (qualify OR match). Remembered per-team so reopening the same team
  // doesn't re-ask; a fresh scan (new token) always asks again.
  const teamPlayers = (team?.info?.players || []).filter(Boolean)
  if (teamPlayers.length && !mePlayer) {
    return <PlayerPick teamName={team?.info?.name || ref.teamName || ref.teamId} players={teamPlayers}
      onPick={(p) => { try { localStorage.setItem(meKey, p) } catch { /* ignore */ }; setMePlayer(p) }} />
  }
  // ROUTE by the TEAM's own qualifying status:
  //   • ALREADY qualified — by the app OR by staff entering the card manually
  //     (score on the qualifying pool, the team info, or the registration) →
  //     straight to the live match/team status (UNCHANGED).
  //   • NOT yet qualified → land on their TEAM HOME too, free to browse. A clear
  //     "Start Qualifying" button there opens the qualifying scorecard when the
  //     group is ready; until then the team stays pending. `?q=1` still forces
  //     qualify straight away (test).
  const qualified = teamIsQualified(tournament, ref.teamId, team?.info)
  const canQualify = !qualified && qualifyingOpen(tournament)   // eligible to start a qualifying round
  const showQualify = !qualified && (qualifyingNow || tqParams.get('q') === '1')
  if (showQualify) {
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return (
      <TokenQualify
        teamName={team?.info?.name || ref.teamName || ref.teamId}
        players={team?.info?.players || []}
        me={mePlayer}
        pars={parsFor(tournament)}
        teamLink={`${origin}/play?t=${encodeURIComponent(tid)}&tk=${encodeURIComponent(token)}`}
        storageKey={`tq_${tid}_${ref.teamId}`}
        onBack={() => setQualifyingNow(false)}
        onSubmit={(p) => submitTeamQualifying(tid, ref.teamId, { ...p, teamName: team?.info?.name || ref.teamName })}
      />
    )
  }
  // The home shows the two choice buttons (Start Qualifying / Qualify Later); the
  // persistent follow-pill lives globally (QualifyFollow) and is hidden on /play,
  // so there's exactly ONE "Start Qualifying" on this screen.
  return (
    <TeamStatusView
      tournament={tournament} tid={tid}
      flightId={ref.flightId} teamId={ref.teamId} team={team} flight={flight}
      teamName={team?.info?.name || ref.teamName || 'Your team'} onSwitch={null}
      mePlayer={mePlayer}
      notQualified={canQualify}
      onStartQualifying={() => setQualifyingNow(true)}
      saveLink={`${typeof window !== 'undefined' ? window.location.origin : ''}/play?t=${encodeURIComponent(tid)}&tk=${encodeURIComponent(token)}`}
      onSwitchPlayer={() => { try { localStorage.removeItem(meKey) } catch { /* ignore */ }; setMePlayer('') }}
    />
  )
}

// "Save your team QR" — long-press to save it to Photos and re-open the app
// (bound to this team) anytime. Collapsed by default so it doesn't crowd the home.
function SaveQrToggle({ link }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="mt-4 text-center">
      <button onClick={() => setOpen((v) => !v)}
        className="font-extrabold rounded-full border px-4 py-2 text-sm active:scale-[0.98]"
        style={{ color: GREEN, borderColor: GOLD, background: 'transparent' }}>
        📱 {open ? 'Hide team QR' : 'Save team QR'}
      </button>
      {open ? (
        <div className="mt-3 rounded-2xl p-4" style={{ background: '#FFFFFF', border: `1px solid ${GOLD}66` }}>
          <TeamQrImage url={link} size={190} />
          <p className="mt-3 text-xs font-semibold" style={{ color: GREEN }}>
            Press &amp; hold the code → <b>Save to Photos</b>. Open it from Photos anytime to re-launch the app on your team — no re-scan.
          </p>
        </div>
      ) : null}
    </div>
  )
}

// "Which player are you?" — pick your player after landing on the team, before
// the match/qualify view. Remembered per team; a fresh scan asks again.
function PlayerPick({ teamName, players, onPick }) {
  return (
    <Shell>
      <Card>
        <Badge>Your team</Badge>
        <h1 className="text-2xl font-extrabold mt-2 mb-1" style={{ color: GREEN }}>{lastNames(teamName)}</h1>
        <p className="text-gray-600 mb-3">Which player are you?</p>
        <div className="space-y-2">
          {players.map((p) => (
            <button key={p} onClick={() => onPick(p)}
              className="w-full text-left rounded-lg px-3.5 py-3 font-bold transition active:scale-[0.99]"
              style={{ color: GREEN, background: CREAM, border: `1px solid ${GREEN}55` }}>
              {p}
            </button>
          ))}
        </div>
      </Card>
    </Shell>
  )
}

// Shared team status/scorecard view — used by BOTH the qualifying flow and the
// token deep link. `onSwitch` null hides the "switch team" link (token-bound).
function TeamStatusView({ tournament, tid, flightId, teamId, team, flight, teamName, onSwitch, mePlayer, onSwitchPlayer, notQualified, onStartQualifying, saveLink }) {
  const navigate = useNavigate()
  const d = deriveMatch(tournament, flightId, teamId)
  const format = flight?.info?.format || tournament?.info?.format || '4-Ball Best Ball'
  const startingHole = team?.info?.startingHole ?? null
  const matchTime = findMatchTime(tournament, teamId)
  const oppName = d.opponentId
    ? (tournament?.flights?.[flightId]?.teams?.[d.opponentId]?.info?.name || 'TBD')
    : 'TBD'
  // Always-visible "who am I" control — a mis-picked player name is the common
  // real-world mistake, so make correcting it obvious (not a buried gray link).
  // "Change" reopens the player picker (clears mePlayer); it does NOT switch teams.
  const meEl = mePlayer ? (
    <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-sm">
      <span style={{ color: GREEN }}>Playing as <b>{mePlayer}</b></span>
      {onSwitchPlayer ? (
        <button onClick={onSwitchPlayer}
          className="font-extrabold rounded-full border px-3 py-1 active:scale-[0.98]"
          style={{ color: GREEN, borderColor: GOLD, background: 'transparent' }}>
          Wrong name? Change
        </button>
      ) : null}
    </div>
  ) : null
  // Calcutta owner for THIS team (shown to everyone viewing the team, not just
  // the admin) — read from the auction's bids. Appears on the team home below.
  const calcuttaOwner = tournament?.calcutta?.bids?.[teamId]?.buyerName || null
  const ownerEl = calcuttaOwner ? (
    <div className="mt-3 text-center">
      <span className="inline-block rounded-full px-3 py-1.5 text-sm font-bold"
        style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}`, color: GREEN }}>
        🏷️ Calcutta owner: <b>{calcuttaOwner}</b>
      </span>
    </div>
  ) : null
  const switchEl = <>{ownerEl}{meEl}{saveLink ? <SaveQrToggle link={saveLink} /> : null}{onSwitch ? <SwitchLink onClick={onSwitch} /> : null}</>

  // NOT YET QUALIFIED — a clear TWO-BUTTON choice: start qualifying now, OR
  // "Qualify Later" which drops them into the FULL app (the hub: leaderboard,
  // brackets, photos, everything) to browse freely. A persistent "Start
  // Qualifying" pill (global QualifyFollow) then follows them on every screen, so
  // they can qualify the moment they're ready. (Already-qualified teams skip this.)
  if (notQualified) {
    return <Shell><Card>
      <Badge tone="muted">Not yet qualified</Badge>
      <h1 className="text-2xl font-extrabold mt-2 mb-1" style={{ color: GREEN }}>You’re in — welcome!</h1>
      <TeamLine name={teamName} />
      {switchEl}
      <p className="text-gray-600 mt-3">Ready to play your qualifying round now, or want to look around the app first? Your call — you can qualify anytime.</p>

      <button
        onClick={onStartQualifying}
        className="mt-5 w-full rounded-xl py-4 text-lg font-extrabold transition active:scale-[0.99]"
        style={{ background: GREEN, color: '#FFFFFF' }}>
        ▶ Start Qualifying
      </button>
      <button
        onClick={() => navigate(`/hub?t=${encodeURIComponent(tid)}`)}
        className="mt-3 w-full rounded-xl py-4 text-lg font-extrabold border transition active:scale-[0.99]"
        style={{ background: 'transparent', color: GREEN, borderColor: GREEN }}>
        Qualify Later →
      </button>
      <p className="text-xs text-gray-500 text-center mt-4">“Qualify Later” opens the full app — leaderboard, brackets, photos and more. A <b>Start Qualifying</b> button follows you on every screen so you can play your round whenever your group is ready.</p>
    </Card></Shell>
  }

  if (d.state === 'eliminated') {
    return <Shell><Card>
      <Badge tone="muted">{d.roundLabel}</Badge>
      <h1 className="text-2xl font-extrabold mt-2 mb-1" style={{ color: GREEN }}>Your run is complete</h1>
      <p className="text-gray-600">Thanks for playing. Final results are posted on the leaderboard.</p>
      <TeamLine name={teamName} />{switchEl}
    </Card></Shell>
  }
  if (d.state === 'champion') {
    return <Shell><Card>
      <div className="text-5xl mb-2">🏆</div>
      <h1 className="text-2xl font-extrabold mb-1" style={{ color: GREEN }}>Flight Champion!</h1>
      <p className="text-gray-600">Congratulations — you won your flight.</p>
      <TeamLine name={teamName} />{switchEl}
    </Card></Shell>
  }
  if (d.state === 'waiting_next' || d.state === 'not_seeded') {
    return <Shell><Card>
      <Badge>{d.state === 'waiting_next' ? 'Match won' : 'In the field'}</Badge>
      <h1 className="text-2xl font-extrabold mt-2 mb-1" style={{ color: GREEN }}>
        {d.state === 'waiting_next' ? 'Waiting on your next match' : 'You’re in the field'}
      </h1>
      <p className="text-gray-600">Your next opponent and tee assignment will appear here once the pairing is set.</p>
      <TeamLine name={teamName} />{switchEl}
    </Card></Shell>
  }
  return (
    <Shell>
      <Card>
        <Badge>{d.roundLabel || 'Your match'}</Badge>
        <h1 className="text-2xl font-extrabold mt-2 mb-3" style={{ color: GREEN }}>You’re on the tee</h1>
        <TeamLine name={teamName} />
        {switchEl}

        <dl className="mt-4 space-y-3">
          <Row label="Opponent" value={lastNames(oppName)} />
          <Row label="Match time" value={matchTime || 'Shotgun start'} />
          <Row label="Starting hole" value={startingHole != null ? `Hole ${startingHole}` : 'TBD'} />
          <Row label="Format" value={format} />
        </dl>

        <button
          onClick={() => navigate(`/score?t=${tid}&f=${flightId}&team=${teamId}`)}
          className="mt-6 w-full rounded-xl py-4 text-lg font-extrabold transition active:scale-[0.99]"
          style={{ background: GREEN, color: '#FFFFFF' }}>
          ⛳ I’m on the tee box →
        </button>
        <p className="text-xs text-gray-500 text-center mt-2">Opens your team scorecard.</p>
      </Card>
    </Shell>
  )
}

// ── QUALIFY screen ──────────────────────────────────────────────────────────
function QualifyScreen({ eventId, eventName, teams, onLinked }) {
  const [player1, setPlayer1] = useState('')
  const [player2, setPlayer2] = useState('')
  const [teamName, setTeamName] = useState('')
  const [teamEdited, setTeamEdited] = useState(false)
  const [score, setScore] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  // Auto-suggest team name from the players' last names until edited.
  const suggested = useMemo(() => {
    const last = (n) => (String(n || '').trim().split(/\s+/).pop() || '')
    const a = last(player1), b = last(player2)
    return a && b ? `${a}/${b}` : (a || b || '')
  }, [player1, player2])
  const effectiveTeam = teamEdited ? teamName : suggested

  const existing = Object.entries(teams).filter(([, t]) => t && typeof t === 'object')

  const canGo = player1.trim() && player2.trim() && effectiveTeam.trim() && Number(score) > 0 && !saving

  const go = async () => {
    if (!canGo) return
    setSaving(true); setErr('')
    try {
      const qTeamId = await createQualifyingEventTeam(eventId, {
        teamName: effectiveTeam, player1, player2,
      })
      await submitQualifyingEventResult(eventId, qTeamId, { score: Number(score), teamName: effectiveTeam })
      writeLink(eventId, { qTeamId, teamName: effectiveTeam.trim() })
      onLinked({ qTeamId, teamName: effectiveTeam.trim() })
    } catch (e) {
      setErr(e?.message || 'Could not submit. Check your connection and try again.')
      setSaving(false)
    }
  }

  const join = (qTeamId, name) => {
    writeLink(eventId, { qTeamId, teamName: name })
    onLinked({ qTeamId, teamName: name })
  }

  return (
    <Card>
      <Badge>Qualify</Badge>
      <h1 className="text-2xl font-extrabold mt-2 mb-0.5" style={{ color: GREEN }}>Qualify</h1>
      <p className="text-sm text-gray-600 mb-4">{eventName}</p>

      <div className="space-y-3">
        <Field label="Player 1" value={player1} onChange={setPlayer1} placeholder="First Last" />
        <Field label="Player 2" value={player2} onChange={setPlayer2} placeholder="First Last" />
        <Field
          label="Team Name"
          value={effectiveTeam}
          onChange={(v) => { setTeamEdited(true); setTeamName(v) }}
          placeholder="e.g. Smith/Jones"
        />
        <Field label="Qualifying Score" value={score} onChange={(v) => setScore(v.replace(/[^0-9]/g, ''))} placeholder="e.g. 68" inputMode="numeric" />
      </div>

      {err ? <p className="text-sm text-red-600 mt-3">{err}</p> : null}

      <button
        onClick={go}
        disabled={!canGo}
        className="mt-5 w-full rounded-xl py-4 text-lg font-extrabold transition active:scale-[0.99]"
        style={{ background: GREEN, color: '#FFFFFF', opacity: canGo ? 1 : 0.5 }}>
        {saving ? 'Submitting…' : 'Go'}
      </button>

      {existing.length > 0 ? (
        <div className="mt-6 pt-5 border-t border-gray-200">
          <p className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">Already registered? Join your team</p>
          <div className="space-y-2">
            {existing.map(([id, t]) => (
              <button
                key={id}
                onClick={() => join(id, String(t.teamName || 'Team').trim())}
                className="w-full text-left rounded-lg px-3.5 py-2.5 font-bold transition active:scale-[0.99]"
                style={{ color: GREEN, background: CREAM, border: `1px solid ${GREEN}55` }}>
                {String(t.teamName || 'Team').split('\n')[0]}
                <span className="block text-xs font-normal text-gray-500">
                  {[t.player1, t.player2].filter(Boolean).join(' & ') || 'Tap to join'}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </Card>
  )
}

// ── Small presentational helpers ────────────────────────────────────────────
function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: GREEN }}>
      <div className="w-full max-w-md mx-auto px-4 py-6 flex-1 flex flex-col">
        <div className="relative flex items-center justify-center mb-4">
          <img src={CLUB.logoLight} alt={CLUB.name} className="h-16 w-auto object-contain" style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.4))' }} />
          <button
            onClick={toggleTheme}
            aria-label={DARK ? 'Switch to light mode' : 'Switch to dark mode'}
            className="absolute left-0 top-0 rounded-full font-bold"
            style={{ background: CREAM, color: GREEN, border: `1.5px solid ${GOLD}`, fontSize: 13, padding: '6px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
            {DARK ? '☀️ Light' : '🌙 Dark'}
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}
function Card({ children }) {
  return <div className="rounded-2xl p-5 sm:p-6 shadow-lg" style={{ background: CREAM }}>{children}</div>
}
function Badge({ children, tone }) {
  const muted = tone === 'muted'
  return (
    <span className="inline-block text-[11px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full"
      style={muted
        ? { background: '#F3F4F6', color: '#6B7280' }
        : { background: 'rgba(var(--ge-primary-rgb),0.10)', color: GREEN }}>
      {children}
    </span>
  )
}
function TeamLine({ name }) {
  return <p className="mt-3 text-sm text-gray-500">Team: <span className="font-bold" style={{ color: GREEN }}>{lastNames(String(name || '').split('\n')[0])}</span></p>
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-gray-100 pb-2">
      <dt className="text-xs uppercase tracking-widest font-bold text-gray-500">{label}</dt>
      <dd className="text-base font-extrabold text-right" style={{ color: GREEN }}>{value}</dd>
    </div>
  )
}
function Field({ label, value, onChange, placeholder, inputMode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest font-bold text-gray-500">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        autoCapitalize="words"
        className="mt-1 w-full rounded-lg border px-3.5 py-2.5 text-base focus:outline-none focus:ring-2"
        style={{ borderColor: 'rgba(var(--ge-primary-rgb),0.22)', color: GREEN }}
      />
    </label>
  )
}
function SwitchLink({ onClick }) {
  return (
    <button onClick={onClick} className="mt-5 block mx-auto text-xs font-semibold text-gray-400 hover:text-gray-600 transition">
      Not your team? Switch
    </button>
  )
}
